import {Component, OnInit, ViewChild} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import * as moment from 'moment-timezone';
import { switchMap } from 'rxjs';

import { CalendarModule } from 'primeng/calendar';
import { SharedModule } from 'src/shared/shared.module';

import { AuthenticationService } from 'src/app/security/authentication.service';
import { WorkingHoursExtensionsHttpHandler } from './services/working-hours-extensions-http-handler';
import { WorkingHoursExtensionsService } from './services/working-hours-extensions.service';

import { PlanningService } from '../../../shared/infrastructure/resources/planning.service';
import { UnavailabilityService } from '../../../shared/infrastructure/resources/unavailability.service';
import { getLocalDateString } from '../../../shared/utils/date.utils';

import { MacroSlotService } from 'src/shared/infrastructure/resources/macro-slot.service';
import { DefaultOpeningClosingLastCallHour } from 'src/shared/models/default-opening-closing-hour.interface';
import { ExtensionConfiguration } from 'src/shared/models/extension-configuration.interface';
import { ExtensionResponseDto } from 'src/shared/models/extension-response.interface';
import { WorkingHourExtensions } from '../../../shared/models/working-hours-extension.interface';
import { ModalElementComponent } from '../../../shared/components/organisms/modal/modal-element.component';
import {TooltipModule} from 'primeng/tooltip';



@Component({
  selector: 'app-extension',
  templateUrl: './extension.component.html',
  styleUrl: './extension.component.scss',
  standalone: true,
  imports: [SharedModule, CalendarModule, TooltipModule],
  providers: [PlanningService, UnavailabilityService, MacroSlotService],
})
export class ExtensionComponent implements OnInit {
  extensionForm!: FormGroup;

  roles: string[] = [];
  canAddExtension = false;

  openingTimeOptions: ExtensionConfiguration[] = [];
  lastCallTimeOptions: ExtensionConfiguration[] = [];

  extensionConfig: ExtensionResponseDto = { morning: [], evening: [] };
  extensions: WorkingHourExtensions[] = [];

  dateFormat = 'dd/mm/yy';
  dayPlusOne: Date = new Date();
  firstOpenedDays!: Date;
  defaultOpeningHour: ExtensionConfiguration | undefined;
  defaultLastCallHour: ExtensionConfiguration | undefined;
  defaultOpeningClosingLastCallHour:
    | DefaultOpeningClosingLastCallHour
    | undefined;
  selectedExtensionId: number | undefined;

  @ViewChild('deleteExtensionsConfirmDialog')
  confirmDialogModal!: ModalElementComponent;
  errorObject: string[] | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authenticationService: AuthenticationService,
    private readonly workingHoursExtensionsService: WorkingHoursExtensionsService,
    private readonly workingHoursExtensionsHttpHandler: WorkingHoursExtensionsHttpHandler,
    private readonly planningService: PlanningService,
    private readonly unavailabilityService: UnavailabilityService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.fetchRoles();
    this.fetchExistingExtensions();
    this.fetchExtensionConfig();
    this.setDefaultOpeningDate();
  }

  initForm(): void {
    this.extensionForm = this.formBuilder.group(
      {
        date: [null, Validators.required],
        openingTime: [null, Validators.required],
        lastCallTime: [null, Validators.required],
      },
      { validators: [this.timeCombinationValidator()] },
    );
  }

  private fetchRoles(): void {
    this.authenticationService.getRoles().subscribe((roles: string[]) => {
      this.roles = roles;
      this.canAddExtension = true;
    });
  }

  private fetchExistingExtensions(): void {
    this.workingHoursExtensionsService.getWorkingHoursExtensions().subscribe({
      next: (extensions: WorkingHourExtensions[]) =>
        (this.extensions = extensions),
      error: (err) => console.error('Error fetching extensions:', err),
    });
  }

  fetchExtensionConfig(): void {
    this.workingHoursExtensionsService
      .getWorkingHoursExtensionsConfig()
      .subscribe({
        next: (config: ExtensionResponseDto) => {
          this.extensionConfig = config;
          this.openingTimeOptions = config.morning;
          this.lastCallTimeOptions = config.evening;

          this.extensionForm.patchValue({
            openingTime: this.openingTimeOptions[0] || null,
            lastCallTime: this.lastCallTimeOptions[0] || null,
          });
        },
        error: (err) =>
          console.error('Error fetching extension configuration:', err),
      });
  }

  setDefaultOpeningDate(): void {
    const today = moment();

    this.planningService
      .getPreviousAndNextOpenedDay(today)
      .pipe(
        switchMap((days) => {
          this.dayPlusOne = new Date(days.nextDay);
          return this.unavailabilityService.retrieveNextOrPreviousOpenedDay(
            1,
            this.dayPlusOne,
            true,
          );
        }),
      )
      .subscribe({
        next: (response) => {
          this.firstOpenedDays = new Date(response[0]);
          this.extensionForm.patchValue({ date: this.firstOpenedDays });
          this.fetchDefaultOpeningAndClosingHours();
        },
        error: (err) => console.error('Error retrieving next opened day:', err),
      });
  }

  fetchDefaultOpeningAndClosingHours(): void {
    this.workingHoursExtensionsHttpHandler
    .getDefaultOpeningAndClosingHours()
    .subscribe({
      next: (defaultOpeningClosingLastCallHour: DefaultOpeningClosingLastCallHour) => {
        this.defaultOpeningClosingLastCallHour = defaultOpeningClosingLastCallHour;

        const { defaultOpeningHour, defaultLastCallHour } = defaultOpeningClosingLastCallHour;
        this.defaultOpeningHour = { startHour: defaultOpeningHour };
        this.defaultLastCallHour = { startHour: defaultLastCallHour };

        if (
          defaultOpeningHour &&
          !this.openingTimeOptions.some(option => option.startHour === this.defaultOpeningHour?.startHour)
        ) {
          this.openingTimeOptions.push(this.defaultOpeningHour);
          this.openingTimeOptions.sort((a, b) => a.startHour.localeCompare(b.startHour));
        }
        if (
          defaultLastCallHour &&
          !this.lastCallTimeOptions.some(option => option.startHour === this.defaultLastCallHour?.startHour)
        ) {
          this.lastCallTimeOptions.push(this.defaultLastCallHour);
          this.lastCallTimeOptions.sort((a, b) => a.startHour.localeCompare(b.startHour));
        }

        this.extensionForm.patchValue({
          openingTime: this.defaultOpeningHour || null,
          lastCallTime: this.defaultLastCallHour || null,
        });
      },
      error: (err) =>
        console.error('Error fetching opening/closing hours:', err),
    });
  }

  saveExtension(): void {
    if (!this.extensionForm.valid || !this.extensionForm.value.date) {
      return;
    }

    const formValues = this.extensionForm.value;

    const openingTimeToSend: ExtensionConfiguration | null =
    formValues.openingTime.startHour === this.defaultOpeningClosingLastCallHour?.defaultOpeningHour
      ? null
      : formValues.openingTime;

    const lastCallTimeToSend: ExtensionConfiguration | null =
      formValues.lastCallTime.startHour === this.defaultOpeningClosingLastCallHour?.defaultLastCallHour
        ? null
        : formValues.lastCallTime;

    const newExtension: WorkingHourExtensions = {
      morningExtensionStart: openingTimeToSend,
      eveningExtensionStart: lastCallTimeToSend,
      extensionDay: getLocalDateString(formValues.date),
    };

    this.workingHoursExtensionsHttpHandler.saveWorkingHoursExtension(newExtension).subscribe({
      next: () => this.fetchExistingExtensions(),
      error: (err) => console.error('Error saving extension:', err),
    });
  }

  timeCombinationValidator() {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const openingTime: ExtensionConfiguration = formGroup.get('openingTime')?.value;
      const lastCallTime: ExtensionConfiguration = formGroup.get('lastCallTime')?.value;

      if (
        openingTime?.startHour === this.defaultOpeningHour?.startHour &&
        lastCallTime?.startHour === this.defaultLastCallHour?.startHour
      ) {
        return { invalidTimeCombination: true };
      }
      return null;
    };
  }

  deleteExtensions(id: number | undefined): void {
    if (id) {
      this.workingHoursExtensionsHttpHandler
        .deleteWorkingHoursExtension(id)
        .subscribe({
          next: () => this.fetchExistingExtensions(),
          error: (err) => console.error('Error deleting extension:', err),
        });
    }
  }

  canDelete(extensionDateStr: string): boolean {
    const extensionDate = new Date(extensionDateStr);
    return extensionDate >= this.firstOpenedDays;
  }

  getDeleteButtonTooltip(extensionDateStr: string): string {
    if (this.canDelete(extensionDateStr)) {
      return  'Supprimer l\'extension';
    }
    return 'La suppression de l\'extension n\'est pas autorisée les jours J et J+1';
  }
}
